import React, { useCallback, useMemo, useState } from "react";
import Input from "../../../../components/FormComponents/Input";
import { useForm } from "react-hook-form";
import useMyAccount from "../../../../hooks/useMyAccount";
import { hasImage } from "../../../Products/components/ProductItem";
import { stripNonNumeric } from "../../../../util/stripNonNumeric";
import { formatCurrency } from "../../../../util/formatters";
import { API_URLS, DEFAULT_HEADERS } from "../../../../util/constants";
import { handleApiError } from "../../../../util/errorHandler";
import { useAuth } from "../../../../hooks/useAuth";
import { Link } from "react-router-dom";
import {
  buildWhatsAppMessageMasterToBuyerInList,
  buildWhatsAppMessageToLinkedUnit,
  buildWhatsAppMessageToUnitMainContact,
  buildWhatsAppMessageToUnitSeller,
  generateWhatsAppLink,
} from "../../../../util/whatsapp";

const toCompanyId = (raw) => {
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const extractCompanyIdFromItem = (c) =>
  toCompanyId(c?.company_id ?? c?.id_company ?? c?.id);

const extractParentIdFromItem = (c) =>
  toCompanyId(c?.parent_company_id ?? c?.id_company_parent ?? c?.parent_id);

const normalizeChildren = (c) => {
  if (Array.isArray(c?.children) && c.children.length > 0) return c.children;
  if (Array.isArray(c?.subcompanies) && c.subcompanies.length > 0) return c.subcompanies;
  if (Array.isArray(c?.company_branches) && c.company_branches.length > 0) return c.company_branches;
  if (Array.isArray(c?.children)) return c.children;
  if (Array.isArray(c?.subcompanies)) return c.subcompanies;
  if (Array.isArray(c?.company_branches)) return c.company_branches;
  return [];
};

/**
 * Constrói árvore a partir de `company_sub_companies` aceitando dois formatos:
 *  - Aninhado: itens com `children`/`subcompanies`.
 *  - Plano com parent: itens com `parent_company_id` apontando para outro `company_id` da lista
 *    (ou para o root quando não bater com nenhum).
 * Retorna a lista de nós de primeiro nível já com `children` recursivos.
 */
const buildCompanyTree = (list, rootCompanyId) => {
  if (!Array.isArray(list) || list.length === 0) return [];

  // 1. Se algum item já vier com children/subcompanies, assumimos formato aninhado e só
  //    garantimos a propriedade `children` em todos os nós.
  const hasNested = list.some((c) => normalizeChildren(c).length > 0);
  if (hasNested) {
    const ensure = (c) => ({
      ...c,
      children: normalizeChildren(c).map(ensure),
    });
    return list.map(ensure);
  }

  // 2. Caso contrário, tenta montar árvore via parent_company_id.
  const byId = new Map();
  list.forEach((c) => {
    const id = extractCompanyIdFromItem(c);
    if (id != null) byId.set(id, { ...c, children: [] });
  });

  const roots = [];
  byId.forEach((node) => {
    const parentId = extractParentIdFromItem(node);
    if (
      parentId != null &&
      parentId !== rootCompanyId &&
      byId.has(parentId) &&
      parentId !== extractCompanyIdFromItem(node)
    ) {
      byId.get(parentId).children.push(node);
    } else {
      roots.push(node);
    }
  });

  return roots;
};

const countTreeNodes = (nodes) => {
  if (!Array.isArray(nodes)) return 0;
  let total = 0;
  nodes.forEach((n) => {
    total += 1;
    total += countTreeNodes(n?.children);
  });
  return total;
};

const collectIdsWithChildren = (nodes, acc = []) => {
  if (!Array.isArray(nodes)) return acc;
  nodes.forEach((n) => {
    const id = extractCompanyIdFromItem(n);
    const children = Array.isArray(n?.children) ? n.children : [];
    if (id != null && children.length > 0) acc.push(id);
    collectIdsWithChildren(children, acc);
  });
  return acc;
};

const CompanyDetails = () => {
  const {
    register,
    formState: { errors },
  } = useForm();

  const { companyData, userData } = useMyAccount();
  const { token } = useAuth();

  const isMasterUser = Boolean(userData?.is_master_user ?? userData?.is_master);

  const myCompanyName = companyData?.company_name ?? userData?.company_name ?? "";

  const rootCompanyId = extractCompanyIdFromItem(companyData ?? {});

  const subCompaniesTree = useMemo(() => {
    const list = Array.isArray(companyData?.company_sub_companies)
      ? companyData.company_sub_companies
      : [];
    return buildCompanyTree(list, rootCompanyId);
  }, [companyData?.company_sub_companies, rootCompanyId]);

  const totalLinkedCompanies = useMemo(
    () => countTreeNodes(subCompaniesTree),
    [subCompaniesTree]
  );

  const hasMultipleLevels = useMemo(
    () => subCompaniesTree.some((n) => Array.isArray(n?.children) && n.children.length > 0),
    [subCompaniesTree]
  );

  const allExpandableIds = useMemo(
    () => collectIdsWithChildren(subCompaniesTree),
    [subCompaniesTree]
  );

  // Estado "collapsed by default = false": guardamos apenas nós explicitamente recolhidos pelo usuário.
  // Assim, qualquer nó com filhos aparece aberto na primeira renderização.
  const [collapsedTreeNodes, setCollapsedTreeNodes] = useState(() => new Set());

  const isNodeOpen = useCallback(
    (id) => (id == null ? false : !collapsedTreeNodes.has(id)),
    [collapsedTreeNodes]
  );

  const toggleNode = useCallback((id) => {
    if (id == null) return;
    setCollapsedTreeNodes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const expandAllNodes = useCallback(() => {
    setCollapsedTreeNodes(new Set());
  }, []);

  const collapseAllNodes = useCallback(() => {
    setCollapsedTreeNodes(new Set(allExpandableIds));
  }, [allExpandableIds]);

  const [expandedCompanyId, setExpandedCompanyId] = useState(null);
  const [buyersByCompanyId, setBuyersByCompanyId] = useState({});
  const [isLoadingBuyersByCompanyId, setIsLoadingBuyersByCompanyId] = useState({});

  const resolveCompanyId = useCallback((c) => extractCompanyIdFromItem(c), []);

  const resolveBuyerIdUser = useCallback((b) => {
    const raw = b?.id_user ?? b?.user_id ?? b?.idUser ?? b?.id ?? null;
    const n = raw == null ? null : Number(raw);
    return Number.isFinite(n) ? n : null;
  }, []);

  const resolveBuyerUid = useCallback((b) => {
    return b?.uid ?? b?.user_uid ?? null;
  }, []);

  const loadCompanyBuyers = useCallback(
    async (companyId, typeCompanyConnected) => {
      if (!companyId) return;
      if (isLoadingBuyersByCompanyId[companyId]) return;
      if (Array.isArray(buyersByCompanyId[companyId])) return;

      setIsLoadingBuyersByCompanyId((prev) => ({ ...prev, [companyId]: true }));
      try {
        const payload = { company_id: companyId };
        // alguns backends exigem token; enviamos quando disponível sem quebrar compatibilidade
        if (token) payload.token = token;
        // all_members_of_company.php exige ao listar outra unidade da rede (0=holding, 1=matriz, 2=filial)
        if (typeCompanyConnected !== undefined && typeCompanyConnected !== null && typeCompanyConnected !== "") {
          const n =
            typeof typeCompanyConnected === "number"
              ? typeCompanyConnected
              : parseInt(String(typeCompanyConnected), 10);
          if (Number.isFinite(n)) payload.type_company_connected = n;
        }

        const response = await fetch(API_URLS.ALL_MEMBERS, {
          method: "POST",
          headers: DEFAULT_HEADERS.JSON_WITH_CORS,
          body: JSON.stringify(payload),
        });

        const data = await response.json().catch(() => null);
        if (!response.ok) {
          const err = new Error(data?.message || data?.error || "Falha ao carregar compradores da empresa");
          err.status = response.status;
          err.response = data;
          throw err;
        }

        if (!data?.success) {
          throw new Error(data?.error || data?.message || "Falha ao carregar compradores da empresa");
        }

        setBuyersByCompanyId((prev) => ({ ...prev, [companyId]: data?.users || [] }));
      } catch (error) {
        handleApiError(error, { context: "CompanyDetails.loadCompanyBuyers", showToast: false });
        setBuyersByCompanyId((prev) => ({ ...prev, [companyId]: [] }));
      } finally {
        setIsLoadingBuyersByCompanyId((prev) => ({ ...prev, [companyId]: false }));
      }
    },
    [buyersByCompanyId, isLoadingBuyersByCompanyId, token]
  );

  const toggleCompanyBuyers = useCallback(
    (companyId, typeCompanyConnected) => {
      setExpandedCompanyId((prev) => {
        const next = prev === companyId ? null : companyId;
        if (next) loadCompanyBuyers(next, typeCompanyConnected);
        return next;
      });
    },
    [loadCompanyBuyers]
  );

  const resolveCompanyDisplayName = (c) => {
    if (!c || typeof c !== "object") return "Empresa";
    return (
      c.company_name ||
      c.fantasy_name ||
      c.delivery?.fantasy_name ||
      c.delivery?.company_name ||
      (c.company_id != null ? `Empresa #${c.company_id}` : "Empresa")
    );
  };

  const resolveCompanyImage = (c) => {
    return (
      c?.image ||
      c?.logo ||
      c?.company_image ||
      c?.main_contact?.seller_image ||
      c?.seller?.image ||
      ""
    );
  };

  const resolveCompanyPhone = (c) => {
    return c?.phone || c?.company_phone || c?.delivery?.phone || null;
  };

  const resolveCompanyEmail = (c) => {
    return (
      c?.email ||
      c?.corporate_email ||
      c?.company_email ||
      c?.delivery?.corporate_email ||
      c?.delivery?.email ||
      null
    );
  };

  const resolveCompanyWhatsapp = (c) => {
    return c?.whatsapp || c?.company_whatsapp || c?.seller?.whatsapp || null;
  };

  const resolveMainContactName = (c) => {
    return (
      c?.main_contact?.seller_name ||
      c?.main_contact?.name ||
      c?.seller?.name ||
      null
    );
  };

  const resolveMainContactEmail = (c) => {
    return c?.main_contact?.seller_email || c?.seller?.email || null;
  };

  const resolveMainContactWhatsapp = (c) => {
    return c?.main_contact?.seller_whatsapp || c?.seller?.whatsapp || null;
  };

  const renderCompanyConnectedType = (t) => {
    const n = typeof t === "number" ? t : parseInt(String(t), 10);
    if (Number.isNaN(n)) return t ?? "—";
    // Alinhado ao contrato da API (type_company_connected): 0=holding, 1=matriz, 2=filial
    switch (n) {
      case 0:
        return "Holding";
      case 1:
        return "Matriz";
      case 2:
        return "Filial";
      default:
        return String(n);
    }
  };

  const toNumberOrNull = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const n = typeof value === "number" ? value : Number(value);
    return Number.isFinite(n) ? n : null;
  };

  const renderBuyersSection = ({
    companyId,
    companyRecord,
    emptyMessage,
  }) => {
    if (!companyId) return null;
    const iconStyle = { color: "#EC1F25", fontSize: "16px", lineHeight: 1, verticalAlign: "middle" };
    const isLoading = Boolean(isLoadingBuyersByCompanyId[companyId]);
    const buyersList = Array.isArray(buyersByCompanyId[companyId]) ? buyersByCompanyId[companyId] : [];

    return (
      <div
        className="mt-3"
        style={{
          border: "1px solid var(--border-color-11, #ededed)",
          borderRadius: "4px",
          padding: "14px 16px",
        }}
      >
        {isLoading ? (
          <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
            Carregando compradores...
          </p>
        ) : buyersList.length === 0 ? (
          <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
            {emptyMessage}
          </p>
        ) : (
          <div className="ltn__my-properties-table table-responsive">
            <table className="table mb-0">
              <thead
                style={{
                  borderRight: "hidden",
                  borderLeft: "hidden",
                  borderTop: "hidden",
                }}
              >
                <tr>
                  <th scope="col">Comprador</th>
                  <th scope="col" style={{ textAlign: "center" }}>Contato</th>
                  <th scope="col" style={{ textAlign: "center" }}>Ações</th>
                </tr>
              </thead>
              <tbody
                style={{
                  borderRight: "hidden",
                  borderLeft: "hidden",
                  fontSize: "14px",
                }}
              >
                {buyersList.map((b, bIdx) => {
                  const buyerIdUser = resolveBuyerIdUser(b);
                  const buyerUid = resolveBuyerUid(b);
                  const key = String(buyerIdUser ?? buyerUid ?? bIdx);
                  const whatsapp = b?.cellphone ?? b?.whatsapp ?? null;
                  const email = b?.email ?? null;
                  const buyerWaMessage = buildWhatsAppMessageMasterToBuyerInList({
                    senderCompanyName: myCompanyName,
                    buyerName: b?.full_name || b?.name,
                    targetUnitName: resolveCompanyDisplayName(companyRecord),
                  });
                  const buyerWaHref = whatsapp ? generateWhatsAppLink(whatsapp, buyerWaMessage) : null;
                  return (
                    <tr key={key}>
                      <td style={{ verticalAlign: "middle" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                          <img
                            src={hasImage(b?.image)}
                            alt="Comprador"
                            style={{
                              width: "44px",
                              height: "44px",
                              objectFit: "cover",
                              borderRadius: "4px",
                              border: "1px solid var(--border-color-11, #ededed)",
                            }}
                          />
                          <div>
                            <div style={{ fontWeight: 700, color: "var(--ltn__primary-color)" }}>
                              {b?.full_name || b?.name || "Comprador"}
                            </div>
                            <div className="text-muted" style={{ fontSize: "12px" }}>
                              {b?.is_master ? "Administrador" : "Comprador"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                        <div
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                          }}
                        >
                          {whatsapp && buyerWaHref && (
                            <a
                              href={buyerWaHref}
                              target="_blank"
                              rel="noreferrer"
                              title={`WhatsApp: ${whatsapp}`}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "32px",
                                height: "32px",
                                borderRadius: "4px",
                                border: "1px solid var(--border-color-11, #ededed)",
                                backgroundColor: "#fff",
                              }}
                            >
                              <i className="fab fa-whatsapp" style={iconStyle} />
                            </a>
                          )}
                          {email && (
                            <a
                              href={`mailto:${email}`}
                              title={`E-mail: ${email}`}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "32px",
                                height: "32px",
                                borderRadius: "4px",
                                border: "1px solid var(--border-color-11, #ededed)",
                                backgroundColor: "#fff",
                              }}
                            >
                              <i className="far fa-envelope" style={iconStyle} />
                            </a>
                          )}
                        </div>
                      </td>
                      <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                        {buyerIdUser ? (
                          <Link
                            to={`/minha-conta/perfil/detalhes-empresa/empresa/${companyId}/compradores/${buyerIdUser}`}
                            state={{ buyer: b, company: companyRecord }}
                            title="Ver detalhes do comprador"
                          >
                            <button
                              type="button"
                              className="theme-btn-1 btn btn-effect-1"
                              style={{ padding: "10px 14px", fontSize: "13px" }}
                            >
                              <i className="fas fa-search" style={{ marginRight: "6px" }} />
                              Ver detalhes
                            </button>
                          </Link>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <form id="contact-form" action="mail.php" method="post">
        <div className="row">
          <div className="col-md-6 mb-3">
            <h6>Contato principal</h6>
            <div className="input-item input-item-lock ltn__custom-icon">
              <Input
                type="text"
                register={register}
                fieldName="name"
                placeholder={companyData?.main_contact?.seller_name ?? "—"}
                errors={errors}
                disabled
              />
            </div>
          </div>
          <div className="col-md-6 mb-3">
            <h6>Whatsapp</h6>
            <div className="input-item input-item-lock ltn__custom-icon">
              <Input
                type="text"
                register={register}
                fieldName="phone"
                placeholder={companyData?.whatsapp ?? "—"}
                errors={errors}
                disabled
              />
            </div>
          </div>
          <div className="col-md-6">
            <h6>Telefone</h6>
            <div className="input-item input-item-lock ltn__custom-icon">
              <Input
                type="text"
                register={register}
                fieldName="phone"
                placeholder={companyData?.phone ?? "—"}
                errors={errors}
                disabled
              />
            </div>
          </div>
          <div className="col-md-6 mb-3">
            <h6>E-mail</h6>
            <div className="input-item input-item-lock ltn__custom-icon">
              <Input
                type="email"
                register={register}
                fieldName="email"
                placeholder={companyData?.email ?? "—"}
                errors={errors}
                disabled
              />
            </div>
          </div>
          <div className="col-md-6 mb-3">
            <h6>Nome da Empresa</h6>
            <div className="input-item input-item-lock ltn__custom-icon">
              <Input
                type="text"
                register={register}
                fieldName="companyName"
                placeholder={companyData?.company_name ?? "—"}
                errors={errors}
                disabled
              />
            </div>
          </div>
          <div className="col-md-6 mb-3">
            <h6>Função do contato principal</h6>
            <div className="input-item input-item-lock ltn__custom-icon">
              <Input
                type="text"
                register={register}
                fieldName="contactFunction"
                placeholder={companyData?.main_contact_function ?? "—"}
                errors={errors}
                disabled
              />
            </div>
          </div>
          <div className="col-md-6 mb-3">
            <h6>CNPJ</h6>
            <div className="input-item input-item-lock ltn__custom-icon">
              <Input
                type="text"
                register={register}
                fieldName="cnpj"
                placeholder={companyData?.cnpj ?? "—"}
                errors={errors}
                disabled
              />
            </div>
          </div>
        </div>
        <p className="form-messege mb-0 mt-20" />
      </form>
      {
        isMasterUser && (
          <div className="mt-5">
            <div className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: "12px" }}>
              <h4 className="title-2 mb-0" style={{ color: "#000" }}>
                Empresas vinculadas
              </h4>
              <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                <span className="text-muted" style={{ fontSize: "14px" }}>
                  {totalLinkedCompanies} {totalLinkedCompanies === 1 ? "empresa" : "empresas"}
                </span>
                {hasMultipleLevels && (
                  <div style={{ display: "inline-flex", gap: "8px" }}>
                    <button
                      type="button"
                      className="theme-btn-1 btn btn-effect-1"
                      style={{ padding: "8px 14px", fontSize: "13px" }}
                      onClick={expandAllNodes}
                      title="Expandir todas as empresas vinculadas"
                    >
                      Expandir tudo
                    </button>
                    <button
                      type="button"
                      className="theme-btn-2 btn btn-effect-1"
                      style={{ padding: "8px 14px", fontSize: "13px" }}
                      onClick={collapseAllNodes}
                      title="Recolher todas as empresas vinculadas"
                    >
                      Recolher tudo
                    </button>
                  </div>
                )}
              </div>
            </div>

            {subCompaniesTree.length === 0 ? (
              <p className="text-muted mb-0 mt-2" style={{ fontSize: "14px" }}>
                Nenhuma empresa vinculada encontrada para esta empresa.
              </p>
            ) : (
              <div className="mt-3">
                {(() => {
                  const renderSubCompanyNode = (c, depth, idx) => {
                    const companyName = resolveCompanyDisplayName(c);
                    const cnpj = c?.cnpj ?? c?.company_cnpj ?? null;
                    const typeLabel = renderCompanyConnectedType(c?.type_company_connected);
                    const cashbackAmount =
                      c?.cashback_amount ?? c?.cashback ?? c?.cashback_balance ?? null;
                    const buyingLimitTotal = toNumberOrNull(
                      c?.buying_limit ?? c?.user_buying_limit ?? c?.buy_limit
                    );
                    const buyingLimitAvailable = toNumberOrNull(
                      c?.buying_limit_available ?? c?.user_buying_limit_available
                    );
                    const buyingLimitUsedLast30Days = toNumberOrNull(
                      c?.buying_limit_used_last_30_days ?? c?.user_buying_limit_used_last_30_days
                    );
                    const hasLimits =
                      buyingLimitTotal !== null ||
                      buyingLimitAvailable !== null ||
                      buyingLimitUsedLast30Days !== null;
                    const limitPct =
                      buyingLimitTotal && buyingLimitTotal > 0 && buyingLimitAvailable !== null
                        ? Math.max(
                            0,
                            Math.min(100, Math.round((buyingLimitAvailable / buyingLimitTotal) * 100))
                          )
                        : null;

                    const phone = resolveCompanyPhone(c);
                    const email = resolveCompanyEmail(c);
                    const whatsapp = resolveCompanyWhatsapp(c);
                    const mainContactName = resolveMainContactName(c);
                    const mainContactEmail = resolveMainContactEmail(c);
                    const mainContactWhatsapp = resolveMainContactWhatsapp(c);
                    const seller = c?.seller && typeof c.seller === "object" ? c.seller : null;
                    const sellerName = seller?.name ?? null;
                    const sellerEmail = seller?.email ?? null;
                    const sellerWhatsapp = seller?.whatsapp ?? null;
                    const sellerCellphone = seller?.cellphone ?? null;
                    const sellerImage = seller?.image ?? "";
                    const subCompanyId = resolveCompanyId(c);

                    const masterUser =
                      c?.master_user && typeof c.master_user === "object" ? c.master_user : null;
                    const masterUserName = masterUser?.full_name ?? masterUser?.name ?? null;
                    const masterUserEmail = masterUser?.email ?? null;
                    const masterUserWhatsapp =
                      masterUser?.whatsapp ?? masterUser?.cellphone ?? null;

                    const unitWhatsappHref = whatsapp
                      ? generateWhatsAppLink(
                          whatsapp,
                          buildWhatsAppMessageToLinkedUnit({
                            senderCompanyName: myCompanyName,
                            targetUnitName: companyName,
                          })
                        )
                      : null;
                    const mainContactWhatsappHref = mainContactWhatsapp
                      ? generateWhatsAppLink(
                          mainContactWhatsapp,
                          buildWhatsAppMessageToUnitMainContact({
                            senderCompanyName: myCompanyName,
                            targetUnitName: companyName,
                          })
                        )
                      : null;
                    const sellerPrimaryWhatsapp = sellerWhatsapp || sellerCellphone;
                    const sellerWhatsappHref = sellerPrimaryWhatsapp
                      ? generateWhatsAppLink(
                          sellerPrimaryWhatsapp,
                          buildWhatsAppMessageToUnitSeller({
                            senderCompanyName: myCompanyName,
                            targetUnitName: companyName,
                            sellerName: sellerName,
                          })
                        )
                      : null;

                    const masterUserWhatsappHref = masterUserWhatsapp
                      ? generateWhatsAppLink(
                          masterUserWhatsapp,
                          buildWhatsAppMessageMasterToBuyerInList({
                            senderCompanyName: myCompanyName,
                            buyerName: masterUserName,
                            targetUnitName: companyName,
                          })
                        )
                      : null;

                    const children = Array.isArray(c?.children) ? c.children : [];
                    const hasChildren = children.length > 0;
                    const isOpen = isNodeOpen(subCompanyId);

                    const typeColors = {
                      Holding: { bg: "rgba(0, 0, 0, 0.06)", color: "var(--ltn__primary-color)" },
                      Matriz: { bg: "rgba(236, 31, 37, 0.10)", color: "var(--ltn__secondary-color)" },
                      Filial: { bg: "rgba(92, 114, 125, 0.12)", color: "var(--ltn__paragraph-color)" },
                    };
                    const badgeColor = typeColors[typeLabel] ?? typeColors.Holding;

                    const cardPadding = depth === 0 ? "20px 22px" : "16px 18px";
                    const avatarSize = depth === 0 ? 56 : 48;

                    const iconLink = (href, iconClass, title, external = false) => (
                      <a
                        href={href}
                        target={external ? "_blank" : undefined}
                        rel={external ? "noreferrer" : undefined}
                        title={title}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "32px",
                          height: "32px",
                          borderRadius: "4px",
                          border: "1px solid var(--border-color-11, #ededed)",
                          backgroundColor: "#fff",
                          color: "var(--ltn__secondary-color)",
                          fontSize: "13px",
                          flexShrink: 0,
                          transition: "all 0.2s ease",
                        }}
                      >
                        <i className={iconClass} />
                      </a>
                    );

                    return (
                      <div
                        key={String(subCompanyId ?? `${depth}-${idx}`)}
                        style={{
                          marginBottom: "16px",
                          backgroundColor: "#fff",
                          border: "2px solid var(--border-color-11, #ededed)",
                          borderRadius: "4px",
                          padding: cardPadding,
                        }}
                      >
                        {/* HEADER: avatar + nome + tipo + metadata + ações */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "14px",
                            flexWrap: "wrap",
                          }}
                        >
                          <img
                            src={hasImage(resolveCompanyImage(c))}
                            alt="Empresa"
                            style={{
                              width: `${avatarSize}px`,
                              height: `${avatarSize}px`,
                              borderRadius: "4px",
                              objectFit: "cover",
                              border: "1px solid var(--border-color-11, #ededed)",
                              flexShrink: 0,
                            }}
                          />
                          <div style={{ flex: "1 1 280px", minWidth: 0 }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                flexWrap: "wrap",
                                marginBottom: "4px",
                              }}
                            >
                              {/* {typeLabel && (
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    padding: "2px 10px",
                                    borderRadius: "999px",
                                    fontSize: "11px",
                                    fontWeight: 600,
                                    letterSpacing: "0.3px",
                                    textTransform: "uppercase",
                                    backgroundColor: badgeColor.bg,
                                    color: badgeColor.color,
                                    lineHeight: 1.4,
                                  }}
                                  title={`Tipo: ${typeLabel}`}
                                >
                                  {typeLabel}
                                </span>
                              )} */}
                            </div>
                            <h5
                              style={{
                                color: "var(--ltn__primary-color)",
                                margin: 0,
                                marginBottom: "4px",
                                fontSize: depth === 0 ? "18px" : "16px",
                                fontWeight: 700,
                                lineHeight: 1.25,
                                wordBreak: "break-word",
                              }}
                            >
                              {companyName}
                            </h5>
                            <div
                              style={{
                                display: "flex",
                                gap: "12px",
                                flexWrap: "wrap",
                                fontSize: "12px",
                                color: "var(--ltn__paragraph-color)",
                              }}
                            >
                              {cnpj && (
                                <span>
                                  CNPJ: <strong style={{ color: "var(--ltn__primary-color)" }}>{cnpj}</strong>
                                </span>
                              )}
                              {cashbackAmount != null && cashbackAmount !== "" && (
                                <span>
                                  Cashback:{" "}
                                  <strong style={{ color: "var(--ltn__secondary-color)" }}>
                                    {formatCurrency(cashbackAmount)}
                                  </strong>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* LIMITE: barra de progresso compacta */}
                        {hasLimits && (
                          <div style={{ marginTop: "14px" }}>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                fontSize: "12px",
                                color: "var(--ltn__paragraph-color)",
                                marginBottom: "6px",
                                gap: "12px",
                                flexWrap: "wrap",
                              }}
                            >
                              <span>
                                Limite disponível:{" "}
                                <strong style={{ color: "var(--ltn__primary-color)" }}>
                                  {formatCurrency(buyingLimitAvailable ?? 0)}
                                </strong>{" "}
                                / {formatCurrency(buyingLimitTotal ?? 0)}
                              </span>
                              {buyingLimitUsedLast30Days != null && (
                                <span>
                                  Usado (30d):{" "}
                                  <strong style={{ color: "var(--ltn__secondary-color)" }}>
                                    {formatCurrency(buyingLimitUsedLast30Days)}
                                  </strong>
                                </span>
                              )}
                            </div>
                            {limitPct !== null && (
                              <div
                                style={{
                                  height: "6px",
                                  borderRadius: "4px",
                                  backgroundColor: "var(--border-color-11, #ededed)",
                                  overflow: "hidden",
                                }}
                              >
                                <div
                                  style={{
                                    height: "100%",
                                    width: `${limitPct}%`,
                                    backgroundColor:
                                      limitPct > 20
                                        ? "var(--ltn__primary-color)"
                                        : "var(--ltn__secondary-color)",
                                    transition: "width 0.2s ease",
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        )}

                        {/* CONTATO PRINCIPAL */}
                        {(mainContactName || mainContactWhatsapp || mainContactEmail) && (
                          <div
                            style={{
                              marginTop: "14px",
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                              flexWrap: "wrap",
                              fontSize: "13px",
                            }}
                          >
                            <span style={{ color: "var(--ltn__paragraph-color)" }}>Contato principal:</span>
                            <strong style={{ color: "var(--ltn__primary-color)" }}>
                              {mainContactName || "—"}
                            </strong>
                            <div style={{ display: "inline-flex", gap: "6px", alignItems: "center" }}>
                              {whatsapp && unitWhatsappHref &&
                                iconLink(unitWhatsappHref, "fab fa-whatsapp", `WhatsApp: ${whatsapp}`, true)}
                              {!whatsapp && mainContactWhatsapp && mainContactWhatsappHref &&
                                iconLink(
                                  mainContactWhatsappHref,
                                  "fab fa-whatsapp",
                                  `WhatsApp: ${mainContactWhatsapp}`,
                                  true
                                )}
                              {phone &&
                                iconLink(`tel:${stripNonNumeric(phone)}`, "fas fa-phone fa-rotate-90", `Telefone: ${phone}`)}
                              {email && iconLink(`mailto:${email}`, "far fa-envelope", `E-mail: ${email}`)}
                              {!email && mainContactEmail &&
                                iconLink(
                                  `mailto:${mainContactEmail}`,
                                  "far fa-envelope",
                                  `E-mail: ${mainContactEmail}`
                                )}
                            </div>
                          </div>
                        )}

                        {/* USUÁRIO MASTER DA UNIDADE (compacto) */}
                        {masterUser && (masterUserName || masterUserEmail || masterUserWhatsapp) && (
                          <div
                            style={{
                              marginTop: "12px",
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                              flexWrap: "wrap",
                              fontSize: "13px",
                              padding: "10px 12px",
                              border: "1px solid var(--border-color-11, #ededed)",
                              borderRadius: "4px",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "36px",
                                height: "36px",
                                borderRadius: "4px",
                                border: "1px solid var(--border-color-11, #ededed)",
                                color: "var(--ltn__secondary-color)",
                                backgroundColor: "#fff",
                                flexShrink: 0,
                              }}
                              title="Usuário master da unidade"
                            >
                              <i className="fas fa-user-shield" />
                            </span>
                            <span style={{ color: "var(--ltn__paragraph-color)" }}>
                              Usuário master:
                            </span>
                            <strong style={{ color: "var(--ltn__primary-color)" }}>
                              {masterUserName || "Não informado"}
                            </strong>
                            <div
                              style={{
                                display: "inline-flex",
                                gap: "6px",
                                alignItems: "center",
                                marginLeft: "auto",
                              }}
                            >
                              {masterUserWhatsapp &&
                                masterUserWhatsappHref &&
                                iconLink(
                                  masterUserWhatsappHref,
                                  "fab fa-whatsapp",
                                  `WhatsApp: ${masterUserWhatsapp}`,
                                  true
                                )}
                              {masterUserEmail &&
                                iconLink(
                                  `mailto:${masterUserEmail}`,
                                  "far fa-envelope",
                                  `E-mail: ${masterUserEmail}`
                                )}
                            </div>
                          </div>
                        )}

                        {/* VENDEDOR (compacto) */}
                        {seller && (sellerName || sellerWhatsapp || sellerEmail || sellerCellphone) && (
                          <div
                            style={{
                              marginTop: "12px",
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                              flexWrap: "wrap",
                              fontSize: "13px",
                              padding: "10px 12px",
                              border: "1px solid var(--border-color-11, #ededed)",
                              borderRadius: "4px",
                            }}
                          >
                            <img
                              src={hasImage(sellerImage)}
                              alt="Vendedor responsável"
                              style={{
                                width: "36px",
                                height: "36px",
                                borderRadius: "4px",
                                objectFit: "cover",
                                border: "1px solid var(--border-color-11, #ededed)",
                                flexShrink: 0,
                              }}
                            />
                            <span style={{ color: "var(--ltn__paragraph-color)" }}>Vendedor:</span>
                            <strong style={{ color: "var(--ltn__primary-color)" }}>
                              {sellerName || "Não informado"}
                            </strong>
                            <div style={{ display: "inline-flex", gap: "6px", alignItems: "center", marginLeft: "auto" }}>
                              {sellerPrimaryWhatsapp && sellerWhatsappHref &&
                                iconLink(
                                  sellerWhatsappHref,
                                  "fab fa-whatsapp",
                                  `WhatsApp: ${sellerPrimaryWhatsapp}`,
                                  true
                                )}
                              {sellerEmail &&
                                iconLink(`mailto:${sellerEmail}`, "far fa-envelope", `E-mail: ${sellerEmail}`)}
                            </div>
                          </div>
                        )}

                        {/* AÇÕES */}
                        {(subCompanyId || hasChildren) && (
                          <div
                            style={{
                              marginTop: "16px",
                              display: "flex",
                              gap: "10px",
                              flexWrap: "wrap",
                              alignItems: "center",
                              borderTop: "1px solid var(--border-color-11, #ededed)",
                              paddingTop: "14px",
                            }}
                          >
                            {subCompanyId && (
                              <button
                                type="button"
                                className={`${expandedCompanyId === subCompanyId ? "theme-btn-2" : "theme-btn-1"} btn btn-effect-1`}
                                onClick={() => toggleCompanyBuyers(subCompanyId, c?.type_company_connected)}
                                title={expandedCompanyId === subCompanyId ? "Ocultar compradores" : "Ver compradores"}
                                style={{ padding: "8px 14px", fontSize: "13px" }}
                              >
                                <i className="fas fa-users" style={{ marginRight: "6px" }} />
                                {expandedCompanyId === subCompanyId ? "Ocultar compradores" : "Ver compradores"}
                              </button>
                            )}
                            {hasChildren && (
                              <button
                                type="button"
                                className={`${isOpen ? "theme-btn-1" : "theme-btn-2"} btn btn-effect-1`}
                                onClick={() => toggleNode(subCompanyId)}
                                title={isOpen ? "Recolher empresas vinculadas" : "Expandir empresas vinculadas"}
                                style={{ padding: "8px 14px", fontSize: "13px" }}
                              >
                                <i
                                  className={isOpen ? "fas fa-chevron-down" : "fas fa-chevron-right"}
                                  style={{ marginRight: "6px" }}
                                />
                                {children.length}{" "}
                                {children.length === 1 ? "filial vinculada" : "filiais vinculadas"}
                              </button>
                            )}
                          </div>
                        )}

                        {/* COMPRADORES (collapse já existente, renderizado aqui) */}
                        {subCompanyId && expandedCompanyId === subCompanyId && (
                          <div style={{ marginTop: "4px" }}>
                            {renderBuyersSection({
                              companyId: subCompanyId,
                              typeCompanyConnected: c?.type_company_connected,
                              companyRecord: c,
                              title: "Compradores",
                              emptyMessage: "Nenhum comprador encontrado para esta empresa vinculada.",
                            })}
                          </div>
                        )}

                        {/* FILHOS (recursivo, indentado por wrapper externo) */}
                        {hasChildren && isOpen && (
                          <div
                            style={{
                              marginTop: "16px",
                              paddingLeft: "16px",
                              borderLeft: "2px solid var(--ltn__secondary-color)",
                            }}
                          >
                            {children.map((child, childIdx) =>
                              renderSubCompanyNode(child, depth + 1, childIdx)
                            )}
                          </div>
                        )}
                      </div>
                    );
                  };

                  return subCompaniesTree.map((c, idx) => renderSubCompanyNode(c, 0, idx));
                })()}
              </div>
            )}
          </div>
        )
      }
    </div>
  );
};

export default CompanyDetails;
